import { formatBytes } from "@/lib";
import {
  BrainCircuit,
  FileArchive,
  FileAudio,
  FileCode,
  FileIcon,
  FileImage,
  FileJson,
  FileQuestion,
  FileSpreadsheet,
  FileText,
  FileType,
  FileVideo,
} from "lucide-react";
import { forwardRef } from "react";

// Utility function to get the appropriate icon based on file extension
export const getFileIcon = (filename) => {
  if (!filename) return FileQuestion;

  const extension = filename.split(".").pop()?.toLowerCase();

  // Image files
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension)) {
    return FileImage;
  }

  // Document files - using FileText for PDF since FilePdf is not available
  if (["pdf"].includes(extension)) {
    return FileText;
  }

  // Text files
  if (["txt", "md", "doc", "docx", "rtf"].includes(extension)) {
    return FileText;
  }

  // Code files
  if (
    [
      "js",
      "jsx",
      "ts",
      "tsx",
      "html",
      "css",
      "py",
      "java",
      "c",
      "cpp",
      "php",
    ].includes(extension)
  ) {
    return FileCode;
  }

  // Data files
  if (["json", "xml"].includes(extension)) {
    return FileJson;
  }

  // Spreadsheet files (Note: xlsx files are not supported)
  if (["csv", "xls"].includes(extension)) {
    return FileSpreadsheet;
  }

  // Archive files
  if (["zip", "rar", "tar", "gz", "7z"].includes(extension)) {
    return FileArchive;
  }

  // Audio files
  if (["mp3", "wav", "ogg", "flac", "m4a"].includes(extension)) {
    return FileAudio;
  }

  // Video files
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(extension)) {
    return FileVideo;
  }

  // Font files
  if (["ttf", "otf", "woff", "woff2"].includes(extension)) {
    return FileType;
  }

  // Default for unknown file types
  return FileIcon;
};

// Get color based on file extension
export const getIconColor = (filename) => {
  if (!filename) return "text-gray-400";

  const extension = filename.split(".").pop()?.toLowerCase();

  // PDF files - red
  if (extension === "pdf") {
    return "text-red-500";
  }

  // Image files - green
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
    return "text-green-400";
  }

  // Code files - blue
  if (
    [
      "js",
      "jsx",
      "ts",
      "tsx",
      "html",
      "css",
      "py",
      "java",
      "c",
      "cpp",
      "php",
    ].includes(extension)
  ) {
    return "text-blue-500";
  }

  // Data files - yellow
  if (["json", "xml"].includes(extension)) {
    return "text-yellow-500";
  }

  // Spreadsheet files - emerald (Note: xlsx files are not supported)
  if (["csv", "xls"].includes(extension)) {
    return "text-emerald-500";
  }

  // Text files - blue-gray
  if (["txt", "md", "doc", "docx", "rtf"].includes(extension)) {
    return "text-blue-400";
  }

  // Archive files
  if (["zip", "rar", "tar", "gz", "7z"].includes(extension)) {
    return "text-purple-400";
  }

  // Media files
  if (["mp3", "wav", "mp4", "mov", "avi"].includes(extension)) {
    return "text-pink-400";
  }

  // Default
  return "text-blue-400";
};

const FileMentionDropdown = forwardRef(
  (
    {
      items = [],
      onSelect,
      triggerPos,
      isOpen,
      selectedIndex = 0,
      onKeyDown,
      onMouseEnter,
      onClose,
    },
    ref
  ) => {
    // Always show dropdown when isOpen is true, ignore items length
    if (!isOpen) return null;

    // Position dropdown above the textarea instead of inside it
    const dropdownStyle = {
      position: "absolute",
      bottom: "calc(100% + 10px)", // Position above the textarea
      left: "20px",
      zIndex: 99999, // Extremely high z-index
      maxHeight: "250px",
      overflowY: "auto",
      width: "calc(100% - 40px)", // Almost full width of container
      backgroundColor: "#1a1a1a", // Darker background
      boxShadow: "0 4px 30px rgba(0, 0, 0, 0.9)", // Very strong shadow
      border: "2px solid #8b5cf6", // Purple border for visibility
    };

    // Separate items by type
    const files = items.filter((item) => item.itemType === "file");
    const memories = items.filter((item) => item.itemType === "memory");

    return (
      <div
        ref={ref}
        className="rounded-md border-2 border-purple-500 bg-zinc-900 file-mention-dropdown"
        style={dropdownStyle}
        onKeyDown={onKeyDown}
        onClick={(e) => {
          // Stop propagation to prevent bubbling
          e.stopPropagation();
        }}
      >
        <div className="p-1">
          {/* Header to show it's open */}
          <div className="px-3 py-2 text-sm font-bold text-purple-400 border-b border-zinc-700 mb-2">
            File & Memory Mentions
          </div>

          {/* Show file section if we have files */}
          {files.length > 0 && (
            <>
              <div className="px-2 py-1 text-xs font-semibold text-zinc-400 uppercase tracking-wider border-b border-zinc-800 mb-1">
                Files
              </div>
              <div role="listbox" aria-label="Available files">
                {files.map((file, index) => {
                  const FileTypeIcon = getFileIcon(file.name);
                  const iconColorClass = getIconColor(file.name);
                  const isPdf = file.name.toLowerCase().endsWith(".pdf");
                  const isSelected = index === selectedIndex;

                  return (
                    <button
                      key={`file-${file.id}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer w-full text-left ${
                        isSelected
                          ? "bg-purple-500/30 text-white"
                          : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                      }`}
                      onClick={(e) => {
                        // Stop propagation to prevent bubbling
                        e.stopPropagation();
                        e.preventDefault();

                        // Select the item THEN close dropdown
                        if (onSelect) {
                          onSelect(file);

                          // Delay closing to ensure selection happens first
                          setTimeout(() => {
                            if (onClose) onClose();
                          }, 50);
                        }
                      }}
                      onMouseEnter={() => onMouseEnter && onMouseEnter(index)}
                    >
                      <div className="relative">
                        <FileTypeIcon className={`h-4 w-4 ${iconColorClass}`} />
                        {isPdf && (
                          <span className="absolute -bottom-1 -right-1 text-[6px] font-bold">
                            PDF
                          </span>
                        )}
                      </div>
                      <span className="truncate">{file.name}</span>
                      {file.size && (
                        <span className="ml-auto text-xs text-zinc-500">
                          {formatBytes(file.size)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Show memory section if we have memories */}
          {memories.length > 0 && (
            <>
              <div className="px-2 py-1 text-xs font-semibold text-zinc-400 uppercase tracking-wider border-b border-zinc-800 mb-1 mt-2">
                Memories
              </div>
              <div role="listbox" aria-label="Available memories">
                {memories.map((memory, index) => {
                  const isSelected = index + files.length === selectedIndex;

                  return (
                    <button
                      key={`memory-${memory.id}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer w-full text-left ${
                        isSelected
                          ? "bg-purple-500/30 text-white"
                          : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                      }`}
                      onClick={(e) => {
                        // Stop propagation to prevent bubbling
                        e.stopPropagation();
                        e.preventDefault();

                        // Select the item THEN close dropdown
                        if (onSelect) {
                          onSelect(memory);

                          // Delay closing to ensure selection happens first
                          setTimeout(() => {
                            if (onClose) onClose();
                          }, 50);
                        }
                      }}
                      onMouseEnter={() =>
                        onMouseEnter && onMouseEnter(index + files.length)
                      }
                    >
                      <BrainCircuit className="h-4 w-4 text-purple-400" />
                      <span className="truncate">{memory.name}</span>
                      <span className="ml-auto text-xs text-zinc-500">
                        Memory
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Always show this message when no items */}
          {!files.length && !memories.length && (
            <div className="px-3 py-3 text-sm text-purple-300">
              No files or memories found. Try uploading some files first.
            </div>
          )}
        </div>
      </div>
    );
  }
);

FileMentionDropdown.displayName = "FileMentionDropdown";

export default FileMentionDropdown;
